import { ComponentType, ReactNode } from 'react';
import { X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { INPUT_SHEET_FILE, INPUT_SHEET_TYPES_FILE, SCHEMA_REF_FILE } from '@/reports/bundles/inputSheet';
import { REPORT_BUNDLE_FIXTURE_DIR } from '@/reports/bundles/types';

/**
 * The designer's chrome, shared by a report's and a schema's: the files'
 * badges, the switch every editor has between its file shown visually and as
 * code, the bar down the left, and the status bar.
 */

export type FileKind = 'ts' | 'tsx' | 'schema' | 'schemaRef' | 'fixture' | 'test' | 'generated' | 'json' | 'other';

export function fileKind(path: string): FileKind {
  if (path === INPUT_SHEET_FILE) return 'schema';
  if (path === SCHEMA_REF_FILE) return 'schemaRef';
  if (path === INPUT_SHEET_TYPES_FILE) return 'generated';
  if (path.startsWith(REPORT_BUNDLE_FIXTURE_DIR) && path.endsWith('.json')) return 'fixture';
  if (path.startsWith('tests/') || /\.test\.tsx?$/.test(path)) return 'test';
  if (path.endsWith('.tsx')) return 'tsx';
  if (path.endsWith('.ts')) return 'ts';
  if (path.endsWith('.json')) return 'json';
  return 'other';
}

const BADGES: Record<FileKind, { text: string; className: string }> = {
  ts: { text: 'TS', className: 'bg-sky-100 text-sky-900' },
  tsx: { text: 'TSX', className: 'bg-sky-100 text-sky-900' },
  schema: { text: 'SCH', className: 'bg-teal-100 text-teal-900' },
  schemaRef: { text: '{ }', className: 'bg-muted text-muted-foreground' },
  fixture: { text: 'FIX', className: 'bg-emerald-100 text-emerald-900' },
  test: { text: 'TEST', className: 'bg-violet-100 text-violet-900' },
  generated: { text: 'GEN', className: 'bg-muted text-muted-foreground' },
  json: { text: '{ }', className: 'bg-muted text-muted-foreground' },
  other: { text: '·', className: 'bg-muted text-muted-foreground' },
};

/** The small letters before a file's name that say what kind of file it is. */
export function FileBadge({ path, className }: { path: string; className?: string }) {
  const badge = BADGES[fileKind(path)];
  return <span aria-hidden="true" className={cn('inline-flex h-4 w-7 shrink-0 items-center justify-center rounded-sm text-[0.55rem] font-bold tracking-wide', badge.className, className)}>{badge.text}</span>;
}

/** A file's name without its folder: `fixtures/within-limit.json` is within-limit.json. */
export const baseName = (path: string) => path.slice(path.lastIndexOf('/') + 1);

export type FileView = 'visual' | 'code' | 'design' | 'try';
const VIEW_LABELS: Record<FileView, string> = { visual: 'designerVisual', code: 'reportBundleCode', design: 'rv2Design', try: 'rv2TryIt' };

/**
 * The same switch in the same place on every editor: the file shown visually
 * or as its code. The schema's has a third, Try it.
 */
export function ViewSwitch({ views, value, onChange }: { views: FileView[]; value: FileView; onChange: (view: FileView) => void }) {
  return (
    <div role="radiogroup" aria-label={I18n.get('designerView')} className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
      {views.map((view) => (
        <button
          key={view}
          type="button"
          role="radio"
          aria-checked={value === view}
          onClick={() => onChange(view)}
          className={cn('h-6 rounded-md px-2.5 text-xs text-muted-foreground transition-colors hover:text-foreground', value === view && 'bg-background font-medium text-foreground shadow-sm')}
        >
          {I18n.get(VIEW_LABELS[view])}
        </button>
      ))}
    </div>
  );
}

/** The row under a file's tab: what file it is, the view switch, and the file's own actions. */
export function FileHeader({ path, note, views, view, onView, children }: { path: string; note?: ReactNode; views: FileView[]; view: FileView; onView: (view: FileView) => void; children?: ReactNode }) {
  return (
    <div className="flex min-h-10 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5">
      <span className="flex min-w-0 items-center gap-2">
        <FileBadge path={path} />
        <span className="truncate font-mono text-xs">{path}</span>
        {note && <span className="truncate text-xs text-muted-foreground">{note}</span>}
      </span>
      {views.length > 1 && <ViewSwitch views={views} value={view} onChange={onView} />}
      {children && <span className="ml-auto flex items-center gap-1.5">{children}</span>}
    </div>
  );
}

/** The open files along the top of the editor; the one showing is filled in, an unsaved one has a dot. */
export function FileTabs({ paths, active, unsaved, onPick, onClose }: { paths: string[]; active: string | null; unsaved: (path: string) => boolean; onPick: (path: string) => void; /** absent: the tabs stay open */ onClose?: (path: string) => void }) {
  return (
    <div role="tablist" aria-label={I18n.get('designerOpenFiles')} className="flex h-9 shrink-0 items-stretch overflow-x-auto border-b bg-muted/40">
      {paths.map((path) => (
        <div key={path} role="presentation" className={cn('flex shrink-0 items-center gap-1 border-r pr-1 pl-3', !onClose && 'pr-3', path === active ? 'bg-background text-foreground' : 'text-muted-foreground hover:bg-muted')}>
          <button type="button" role="tab" aria-selected={path === active} title={path} onClick={() => onPick(path)} className={cn('flex items-center gap-2 py-1 text-xs', path === active && 'font-medium')}>
            <FileBadge path={path} />
            {baseName(path)}
          </button>
          {unsaved(path) && <span role="img" aria-label={I18n.get('reportBundleUnsaved')} className="size-1.5 rounded-full bg-foreground/70" />}
          {onClose && (
            <button type="button" aria-label={I18n.get('designerCloseFile', { file: baseName(path) })} onClick={() => onClose(path)} className="flex size-5 items-center justify-center rounded-sm opacity-60 hover:bg-muted hover:opacity-100">
              <X className="size-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export interface Activity<A extends string> {
  id: A;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** a dot on the icon: something there wants looking at */
  mark?: 'busy' | 'bad' | null;
}

/** The bar down the left: one button per view of the side bar; the open one again folds it away. */
export function ActivityBar<A extends string>({ items, active, onPick }: { items: Activity<A>[]; active: A | null; onPick: (id: A) => void }) {
  return (
    <nav aria-label={I18n.get('designerViews')} className="flex w-11 shrink-0 flex-col border-r bg-muted/40">
      {items.map(({ id, label, icon: Icon, mark }) => (
        <button
          key={id}
          type="button"
          aria-label={label}
          title={label}
          aria-pressed={active === id}
          onClick={() => onPick(id)}
          className={cn('relative flex h-11 items-center justify-center border-l-2 border-transparent text-muted-foreground hover:text-foreground', active === id && 'border-primary bg-background text-foreground')}
        >
          <Icon className="size-[1.1rem]" />
          {mark && <span aria-hidden="true" className={cn('absolute top-2 right-2 size-1.5 rounded-full', mark === 'bad' ? 'bg-red-500' : 'animate-pulse bg-amber-500')} />}
        </button>
      ))}
    </nav>
  );
}

/** A side bar's heading: its name in small capitals, its actions on the right. */
export function SidebarHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b pr-1.5 pl-3">
      <h2 className="text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">{title}</h2>
      {children && <div className="flex items-center gap-0.5">{children}</div>}
    </div>
  );
}

/** REPORT or SCHEMA before the title: which of the two the designer has open. */
export function KindChip({ kind }: { kind: 'report' | 'schema' }) {
  return (
    <span className={cn('shrink-0 rounded-sm border-[1px] px-1.5 text-[0.6rem] font-bold tracking-wider', kind === 'schema' ? 'border-teal-300 bg-teal-50 text-teal-900' : 'border-border text-muted-foreground')}>
      {I18n.get(kind === 'schema' ? 'designerKindSchema' : 'designerKindReport')}
    </span>
  );
}

/** The bar along the bottom: what compiles, what holds, what the preview reads, what is saved. */
export function StatusBar({ children }: { children: ReactNode }) {
  return (
    <footer aria-label={I18n.get('designerStatus')} className="flex h-6 shrink-0 items-center gap-4 overflow-hidden border-t bg-muted/60 px-3 text-[0.7rem] whitespace-nowrap text-muted-foreground">
      {children}
    </footer>
  );
}

/** One item on the status bar; a button when clicking it opens what it is about. */
export function StatusItem({ children, onClick, tone, className }: { children: ReactNode; onClick?: () => void; tone?: 'ok' | 'bad' | 'warn'; className?: string }) {
  const colour = tone === 'bad' ? 'text-red-700' : tone === 'warn' ? 'text-amber-800' : tone === 'ok' ? 'text-emerald-800' : undefined;
  if (!onClick) return <span className={cn('flex items-center gap-1.5', colour, className)}>{children}</span>;
  return <button type="button" onClick={onClick} className={cn('flex items-center gap-1.5 hover:text-foreground hover:underline', colour, className)}>{children}</button>;
}
